import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTrafficPattern } from '@/services/traffic-pattern.service.js';
import { evictStale } from '@/lib/inFlight.js';
import { defaultStageFilter } from '@/components/traffic/StageFilterChip.jsx';
import useIdleAware, { IDLE_POLL_MS } from '@/hooks/useIdleAware.js';

// 60s poll while foregrounded; pauses on visibilitychange, drops to 10-min
// cadence when visible but idle >15 min.
const POLL_MS = 60_000;
const LS_GROUP_KEY = 'tracon:pattern:group_by';
const LS_FILTER_KEY = 'tracon:pattern:filters';

function readLS(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) || fallback; }
  catch { return fallback; }
}

function parseStageParam(raw) {
  if (!raw) return null;
  const stages = raw.split(',').filter(Boolean);
  return stages.length > 0 ? stages : null;
}

export default function useTrafficPattern() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState(() => readLS(LS_GROUP_KEY, 'urgency'));
  const [filters, setFilters] = useState(() => readLS(LS_FILTER_KEY, {}));
  const aborted = useRef(false);
  const timerRef = useRef(null);
  const lastFetchRef = useRef(0);

  const stageFromUrl = parseStageParam(searchParams.get('filter_stage'));
  const [stageFilter, setStageFilterState] = useState(() => stageFromUrl || defaultStageFilter());

  // opts.poll: only the timer's tick passes it (X-Tracon-Poll, lib/poll.js).
  const refresh = useCallback(async (gb, fl, sf, { poll = false } = {}) => {
    const g = gb ?? groupBy;
    const f = fl ?? filters;
    const s = sf ?? stageFilter;
    lastFetchRef.current = Date.now();
    try {
      const d = await getTrafficPattern({ groupBy: g, filters: f, stageFilter: s, poll });
      if (!aborted.current) {
        setData(d);
        setError(null);
      }
    } catch (err) {
      if (!aborted.current) setError(err);
    } finally {
      if (!aborted.current) setLoading(false);
    }
  }, [groupBy, filters, stageFilter]);

  const mutateGroupBy = useCallback((val) => {
    setGroupBy(val);
    localStorage.setItem(LS_GROUP_KEY, JSON.stringify(val));
    refresh(val, filters, stageFilter);
  }, [refresh, filters, stageFilter]);

  const mutateFilters = useCallback((val) => {
    setFilters(val);
    localStorage.setItem(LS_FILTER_KEY, JSON.stringify(val));
    refresh(groupBy, val, stageFilter);
  }, [refresh, groupBy, stageFilter]);

  const mutateStageFilter = useCallback((stages) => {
    setStageFilterState(stages);
    const defaults = defaultStageFilter();
    const isDefault = stages.length === defaults.length
      && defaults.every(s => stages.includes(s))
      && !stages.some(s => !defaults.includes(s));
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (isDefault) {
        next.delete('filter_stage');
      } else {
        next.set('filter_stage', stages.join(','));
      }
      return next;
    }, { replace: true });
    refresh(groupBy, filters, stages);
  }, [refresh, groupBy, filters, setSearchParams]);

  useEffect(() => {
    aborted.current = false;
    refresh();
    return () => { aborted.current = true; };
  }, [refresh]);

  const isIdle = useIdleAware({
    onActive: () => {
      if (Date.now() - lastFetchRef.current > POLL_MS) refresh();
    },
  });

  useEffect(() => {
    function tick() {
      if (document.visibilityState !== 'visible') return;
      if (isIdle() && Date.now() - lastFetchRef.current < IDLE_POLL_MS) return;
      refresh(undefined, undefined, undefined, { poll: true });
    }
    function start() {
      if (timerRef.current) return;
      timerRef.current = setInterval(tick, POLL_MS);
    }
    function stop() {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        // Wake from sleep: drop dedup slots whose promises died with the
        // socket, so this refresh fetches instead of awaiting a corpse.
        evictStale();
        refresh();
        start();
      } else {
        stop();
      }
    }
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, isIdle]);

  return {
    data, error, loading, refresh,
    groupBy, mutateGroupBy,
    filters, mutateFilters,
    stageFilter, mutateStageFilter,
  };
}
